import { z } from 'zod'
import { computed, ref } from 'vue'
import type { SortedField } from '@/types'
import { useQueryParameter } from '@/shared/composables/useQueryParameter'
import { refDebounced } from '@vueuse/core'

export function usePaginationQueryParameters() {
  const lastChangedParameter = ref<'filter'>()
  const pageQueryParameter = useQueryParameter(
    'page',
    { schema: z.number().int().gte(1), fallback: 1 }
  )
  const pageSizeQueryParameter = useQueryParameter(
    'limit',
    { schema: z.number().int().gte(1).lte(250), fallback: 10 }
  )
  const filterQueryParameter = useQueryParameter(
    'filter',
    { schema: z.string().trim().min(1) }
  )
  const sortingQueryParameter = useQueryParameter(
    'sort',
    { schema: z.string().trim().min(1) }
  )

  const debounceDuration = 500
  const debouncedPage = refDebounced(pageQueryParameter, debounceDuration)
  const debouncedPageSize = refDebounced(pageSizeQueryParameter, debounceDuration)
  const debouncedFilter = refDebounced(filterQueryParameter, debounceDuration)
  const debouncedSorting = refDebounced(sortingQueryParameter, debounceDuration)

  return {
    debounced: {
      page: computed(
        () => lastChangedParameter.value === 'filter' ? debouncedPage.value : pageQueryParameter.value
      ),
      pageSize: computed(
        () => lastChangedParameter.value === 'filter' ? debouncedPageSize.value : pageSizeQueryParameter.value
      ),
      filter: computed(
        () => lastChangedParameter.value === 'filter' ? debouncedFilter.value : filterQueryParameter.value
      ),
      sorting: computed(
        () => parseSorting(lastChangedParameter.value === 'filter' ? debouncedSorting.value : sortingQueryParameter.value)
      )
    },
    page: computed({
      get() {
        return pageQueryParameter.value
      },
      set(newValue: number|undefined) {
        lastChangedParameter.value = undefined
        pageQueryParameter.value = newValue
      }
    }),
    pageSize: computed({
      get() {
        return pageSizeQueryParameter.value
      },
      set(newValue: number|undefined) {
        lastChangedParameter.value = undefined
        pageSizeQueryParameter.value = newValue
      }
    }),
    filter: computed({
      get: () => filterQueryParameter.value,
      set(newValue: string) {
        lastChangedParameter.value = 'filter'
        pageQueryParameter.value = undefined
        filterQueryParameter.value = newValue
      }
    }),
    sorting: computed({
      get: () => parseSorting(sortingQueryParameter.value),
      set(newValue: SortedField[]) {
        lastChangedParameter.value = undefined
        pageQueryParameter.value = undefined
        sortingQueryParameter.value = newValue.length > 0
          ? newValue.map(x => `${x.direction === 'desc' ? '-' : ''}${x.field}`).join(',')
          : undefined
      }
    })
  }
}

function parseSorting(value: string|undefined) {
  if (value === undefined) {
    return []
  }

  return value.split(',')
    .map(
      (field: string): SortedField|undefined => {
        const trimmed = field.trim()

        if (trimmed.length < 1) {
          return undefined
        }

        const direction = trimmed[0] === '-' ? 'desc' : 'asc'
        field = field.replace(/^[+-]+/, '')

        return field.length > 0 ? { field, direction } : undefined
      }
    )
    .filter(
      (x: SortedField | undefined): x is SortedField => x !== undefined
    )
}
